import React, { useEffect, useRef, useState } from "react";
import { loginUser } from "../api/Login.api";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ForgotPwdModal from "./ForgotPwdModal.tsx";
import { registerLogin } from "../api/Audit.api.ts";
import videoMain from "../../media/videoMain.mp4";
import fondoImg from "../../media/Fondo.jpg";


interface LoginCredentialsProps {
  buyButtonTouched: boolean;
  scrollToFeatures: () => void;
}

const LoginCredentials: React.FC<LoginCredentialsProps> = ({ buyButtonTouched, scrollToFeatures }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [fade, setFade] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [block, setBlock] = useState(false);

  const navigate = useNavigate();

  const [isForgotPwdModalOpen, setIsForgotPwdModalOpen] = useState(false);

  const handleForgotPasswordClick = () => {
    setIsForgotPwdModalOpen(true);
  }

  const handleCloseForgotPwdModal = () => {
    setIsForgotPwdModalOpen(false);
  };

  const handleBuyButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); 
    scrollToFeatures();
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnded = () => {
      setFade(true);
      setTimeout(() => {
        video.currentTime = 0;
        video.play().catch(e => {
            console.warn("Fallo al intentar hacer loop del video:", e);
            setFade(false); 
        });
        setFade(false);
      }, 800);
    };

    video.addEventListener("ended", handleEnded);

    video.play().catch(e => {
        console.warn("Fallo en el autoPlay inicial:", e);
    });

    return () => video.removeEventListener("ended", handleEnded);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const guidedDemo = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    window.open('https://api.funnelup.io/widget/booking/kKELdhvT5NCGDqA7zpUg', '_blank');
  }

  const logIn = (e: React.MouseEvent<HTMLButtonElement>) => {
  e.preventDefault();
  setLoading(true);
  setBlock(true);

  const data = { email, password };

  loginUser(data)
    .then((data) => {
      const token = data.token;
      localStorage.setItem("authLisualToken", token);

      // Decodificar token
      const decoded = JSON.parse(atob(token.split(".")[1]));
      const role = decoded.role;
      const customerId = decoded.customerId;

      // Registrar el login en la base de datos (Auditoría)
      registerLogin({
        email: email,
        role: role,
        customerId: customerId,
        userId: decoded.id || decoded.sub // Ajustar según la estructura de tu token
      });

      // Redirección según role
      switch (role) {
        case "guest":
          navigate(`/guestDashboard/${customerId}`);
          break;

        case "editor":
        case "sales":
          navigate("/customersAndCamListDashboard");
          break;

        case "defaultCustomerUser":
        case "responsibleUser":
        case "user":  
          navigate(`/customerDashboard/${customerId}`);
          break;

        case "admin":
        case "customerExperience":
        case "sales":
        case "TESTTIMELAPSE":
        case "EC2TIMELAPSE":
          navigate("/adminDashboard");
          break;
          
        case "infra":
          navigate("/monitor");
          break;   

        default:
          toast.error("Rol inválido. Contacta al soporte.");
          break;
      }
    })
    .catch((error) => {
      toast.error("Error al iniciar sesión. Por favor, verifica tus credenciales.", {
        position: "top-center",
      });
      console.error("Error de login:", error);
    })
    .finally(() => {
      setTimeout(() => finishLoading(), 2000);
    });
};


  const finishLoading = () => {
    setLoading(false);
    setBlock(false);
  }

  return (
    <section className="relative min-h-[100vh] pb-24 flex items-center justify-center sm:block sm:pt-12 sm:pb-24"> 
      
      {/* ✅ VIDEO: Oculto por defecto (en mobile), visible a partir de pantallas sm/md. 
        Añadimos z-0 para asegurar el apilamiento correcto con el overlay y el contenido.
      */}
      <video
        ref={videoRef}
        muted
        autoPlay
        playsInline
        className="hidden sm:block absolute top-0 left-0 w-full h-full object-cover z-0"
      >
        <source src={videoMain} type="video/mp4" />
      </video>
      
      {/* ✅ FALLBACK: Visible solo en pantallas sm/md y más pequeñas (mobile). */}
      <div
        className="sm:hidden absolute top-0 left-0 w-full h-full bg-cover bg-center z-0"
        style={{ backgroundImage: `url(${fondoImg})` }}    />
      {/* OVERLAY para Desktop */}
      <div
        className={`absolute inset-0 bg-black z-10 
        ${fade ? "opacity-100" : "opacity-60"} 
        hidden sm:block transition-opacity duration-700`}
      ></div>
      {/* Overlay fijo para Mobile */}
      <div
        className="absolute inset-0 bg-black opacity-30 sm:hidden z-10"
      ></div>


      {/* Contenedor principal de contenido: Aseguramos que ocupe todo el ancho para centrar */}
      <div className="container mx-auto py-12 relative z-20 w-full"> {/* z-20 para estar encima de todo */}
        <div className="flex flex-wrap items-center justify-center sm:justify-between">
          
          {/* TÍTULO Y SUBTÍTULO: Visible solo en pantallas sm/md y más grandes. */}
          <div className="w-full md:w-7/12 lg:w-6/12 xl:w-5/12 text-left mb-8 md:mb-0 px-4 hidden sm:block">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
              Plataforma de marketing y control N°1
            </h1>
            <p className="text-lg sm:text-xl text-white pl-0 sm:pl-[15px]">
              Obras de construcción y seguridad
            </p>
          </div>
          
          {/* FORMULARIO DE LOGIN: Ocupa todo el ancho en mobile. */}
          <div className="w-full md:w-5/12 lg:w-4/12 px-2 sm:px-4">
            <div className="w-full mx-auto" data-aos="fade-up">
              <form className="relative block bg-white p-6 sm:p-8 rounded-lg shadow-xl mx-auto w-full max-w-[320px] sm:max-w-sm">

                <h3 className="text-2xl font-bold mb-6 text-center">Iniciar sesión</h3>

                {/* Inputs de login */}
                <div className="mb-4">
                  <label className="block text-gray-700 font-bold mb-2" htmlFor="course-name-1">
                    Mail
                  </label>
                  <input
                    id="course-name-1"
                    type="email"
                    className="w-full p-3 border rounded text-sm"
                    placeholder="ejemplo@lisual.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 font-bold mb-2" htmlFor="course-email-1">
                    Contraseña
                  </label>
                  <input
                    id="course-email-1"
                    type="password"
                    className="w-full p-3 border rounded text-sm"
                    placeholder="*********"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <div className="text-center">
                  <button
                    className="w-full bg-green-500 text-white font-bold py-3 px-4 rounded-lg hover:bg-green-600 transition duration-300 text-sm"
                    type="submit"
                    onClick={logIn}
                    disabled={block}
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white-500" style={{ display: loading ? 'inline-block' : 'none' }}></div>
                    ) : (loading ? null : "Iniciar sesión")}
                  </button>

                  <small className="text-gray-500 mt-2 block">
                    No recordas tu contraseña?, haz click{" "}
                    <button
                      type="button"
                      className="text-blue-500 hover:underline"
                      onClick={handleForgotPasswordClick}
                    >
                      aquí
                    </button>
                  </small>
                </div>

                {/* Botón Comprar */}
                <button
                  type="button"
                  className="w-full bg-blue-500 text-white font-bold py-3 px-4 rounded-lg hover:bg-green-600 transition duration-300 mt-3 text-sm"
                  onClick={handleBuyButtonClick}
                >
                  Comprar
                </button>

                <button
                  type="button"
                  className="w-full bg-violet-500 text-white font-bold py-3 px-4 rounded-lg hover:bg-green-600 transition duration-300 mt-3 text-sm"
                  onClick={guidedDemo}
                >
                  Demo Guiada
                </button>
              </form>
             </div>
           </div>
         </div>
       </div>

       <ForgotPwdModal 
         isOpen={isForgotPwdModalOpen} 
         onClose={handleCloseForgotPwdModal} 
       />

     </section>
   );
 };

 export default LoginCredentials;
